import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Download, 
  Trash2, 
  FileText, 
  Image as ImageIcon,
  AlertCircle 
} from 'lucide-react';
import { DocumentsService } from '../services/documents-service';
import { FileRemovalDialog } from './FileRemovalDialog';
import { getImageUrl, isImageFile, isPdfFile } from '@/utils/r2-url-transformer';

interface FilePreviewProps {
  documentId: string;
  file: {
    id: string;
    filename: string;
    storage_path: string;
    thumbnail_path: string | null;
    file_type: string;
    file_size: number;
    uploaded_at: string;
  };
  onFileRemoved?: () => void;
}

export function FilePreview({ documentId, file, onFileRemoved }: FilePreviewProps) {
  const [showRemovalDialog, setShowRemovalDialog] = React.useState(false);
  const [isRemoving, setIsRemoving] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [imageError, setImageError] = React.useState(false);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Unknown date';
    }
  };

  const isImage = isImageFile(file.storage_path) || isImageFile(file.file_type);
  const isPdf = isPdfFile(file.storage_path) || isPdfFile(file.file_type);

  const handleDownload = async () => {
    if (!DocumentsService.checkFilePermissions('download')) {
      return;
    }

    try {
      setIsDownloading(true);
      const { download_url } = await DocumentsService.getFileDownloadUrl(documentId);
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = download_url;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
      // You might want to show a toast notification here
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRemoveConfirm = async () => {
    try {
      setIsRemoving(true);
      await DocumentsService.removeDocumentFile(documentId);
      setShowRemovalDialog(false);
      onFileRemoved?.();
    } catch (error) {
      console.error('File removal failed:', error);
      // You might want to show a toast notification here
    } finally {
      setIsRemoving(false);
    }
  };

  const renderFilePreview = () => {
    if (isImage && !imageError) {
      // For images, get the proper public URL
      const imageUrl = getImageUrl(file.thumbnail_path, file.storage_path);
      
      if (imageUrl) {
        return (
          <div className="relative w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
            <img
              src={imageUrl}
              alt={file.filename}
              className="max-w-full max-h-full object-cover"
              onError={() => setImageError(true)}
              onLoad={() => setImageError(false)}
            />
          </div>
        );
      }
    }

    // For PDFs or failed image loads, show a file icon
    return (
      <div className="w-32 h-32 bg-gray-100 rounded-lg flex flex-col items-center justify-center">
        {isPdf ? (
          <FileText className="h-12 w-12 text-red-500 mb-2" />
        ) : isImage ? (
          <ImageIcon className="h-12 w-12 text-gray-400 mb-2" />
        ) : (
          <FileText className="h-12 w-12 text-gray-500 mb-2" />
        )}
        <Badge variant="secondary" className="text-xs">
          {isPdf ? 'PDF' : isImage ? 'IMAGE' : 'FILE'}
        </Badge>
      </div>
    );
  };

  return (
    <>
      <Card className="w-full">
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            {renderFilePreview()}
            
            <div className="flex-1 min-w-0">
              <div className="space-y-2">
                <h3 className="font-medium text-sm truncate" title={file.filename}>
                  {file.filename}
                </h3>
                
                <div className="space-y-1 text-xs text-muted-foreground">
                  <div>{formatFileSize(file.file_size)} • {file.file_type}</div>
                  <div>Uploaded {formatDate(file.uploaded_at)}</div>
                </div>

                {imageError && isImage && (
                  <div className="flex items-center gap-1 text-xs text-amber-600">
                    <AlertCircle className="h-3 w-3" />
                    Image preview unavailable
                  </div>
                )}
              </div>

              <div className="flex gap-2 mt-4">
                {DocumentsService.checkFilePermissions('download') && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDownload}
                    disabled={isDownloading}
                  >
                    <Download className="h-3 w-3 mr-1" />
                    {isDownloading ? 'Downloading...' : 'Download'}
                  </Button>
                )}

                {DocumentsService.checkFilePermissions('remove') && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowRemovalDialog(true)}
                    disabled={isRemoving}
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <FileRemovalDialog
        isOpen={showRemovalDialog}
        onClose={() => setShowRemovalDialog(false)}
        onConfirm={handleRemoveConfirm}
        documentId={documentId}
        file={file}
        isLoading={isRemoving}
      />
    </>
  );
}
